import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import { interactionAggregateLegacyRouteSchema } from './conversations-contracts'
import type { z } from 'zod'

export type InteractionAggregateRoute = z.infer<
  typeof interactionAggregateLegacyRouteSchema
>

export type InteractionAggregateRouteRepository = {
  getLegacyRoute: (
    tenantId: string,
    aggregateId: string
  ) => Promise<InteractionAggregateRoute>
  getLegacyContactRoute?: (
    tenantId: string,
    legacyPatientId: string
  ) => Promise<InteractionAggregateRoute>
}

const forbiddenIdentityCharacters = new Set('.#$/[]')

function exactIdentity(value: string) {
  if (
    !value ||
    value !== value.trim() ||
    value.length > 512 ||
    Array.from(value).some((character) =>
      forbiddenIdentityCharacters.has(character)
    ) ||
    Array.from(value).some(
      (character) =>
        character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
    )
  ) {
    throw new Error('Interaction identity is invalid')
  }
  return value
}

export async function legacyInteractionAggregateId(
  tenantId: string,
  legacyPatientId: string
) {
  const tenant = exactIdentity(tenantId)
  const patient = exactIdentity(legacyPatientId)
  const encoded = new TextEncoder().encode(
    JSON.stringify(['interaction-aggregate-v1', tenant, patient])
  )
  const digest = await globalThis.crypto.subtle.digest('SHA-256', encoded)
  const hexadecimal = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('')
  return `ia_v1_${hexadecimal}`
}

export function createInteractionAggregateRouteRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): InteractionAggregateRouteRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    getLegacyRoute(tenantId, aggregateId) {
      return client.request(
        `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}/legacy-route`,
        { method: 'GET', responseSchema: interactionAggregateLegacyRouteSchema }
      )
    },
    async getLegacyContactRoute(tenantId, legacyPatientId) {
      const aggregateId = await legacyInteractionAggregateId(
        tenantId,
        legacyPatientId
      )
      const route = await client.request(
        `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}/legacy-route`,
        { method: 'GET', responseSchema: interactionAggregateLegacyRouteSchema }
      )
      if (
        route.aggregateId !== aggregateId ||
        route.legacyPatientId !== legacyPatientId
      ) {
        throw new Error('Interaction Aggregate route is not reciprocal')
      }
      return route
    },
  }
}
